import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import type { Lead, LeadStatus, LeadSource } from '../mocks/crm';

const dbStatusToMock: Record<string, LeadStatus> = {
  new: 'New', contacted: 'Contacted', qualified: 'Quoted',
  proposal: 'Quoted', negotiation: 'Negotiating', won: 'Won', lost: 'Lost',
};
const mockStatusToDb: Record<LeadStatus, string> = {
  New: 'new', Contacted: 'contacted', Quoted: 'qualified',
  Negotiating: 'negotiation', Won: 'won', Lost: 'lost',
};
const dbSourceToMock: Record<string, LeadSource> = {
  manual: 'Phone', website: 'Website', referral: 'Referral',
  social: 'Website', email: 'Email', whatsapp: 'WhatsApp', form: 'Website', other: 'Walk-in',
};
const sourceIconMap: Record<string, string> = {
  WhatsApp: 'ri-whatsapp-line', Website: 'ri-global-line', Phone: 'ri-phone-line',
  Email: 'ri-mail-line', Referral: 'ri-share-line', 'Walk-in': 'ri-store-line',
};

function rowToLead(r: Record<string, unknown>): Lead {
  const source = dbSourceToMock[(r.source as string) || 'manual'] || 'Phone';
  return {
    id: r.id as string,
    name: (r.full_name as string) || '',
    email: (r.email as string) || '',
    phone: (r.phone as string) || '',
    country: (r.country as string) || '',
    service: (r.destination as string) || 'Travel Enquiry',
    serviceIcon: 'ri-map-2-line',
    budget: `AED ${((r.budget as number) || 0).toLocaleString()}`,
    budgetNum: (r.budget as number) || 0,
    status: dbStatusToMock[(r.status as string) || 'new'] || 'New',
    source: source as LeadSource,
    sourceIcon: sourceIconMap[source] || 'ri-question-line',
    assignedTo: 'You',
    assignedAvatar: 'YO',
    createdAt: (r.created_at as string) || '',
    updatedAt: (r.updated_at as string) || '',
    notes: [],
    tags: (r.tags as string[]) || [],
    travelDate: (r.travel_date as string) || '',
    pax: ((r.pax_adults as number) || 1) + ((r.pax_children as number) || 0),
  };
}

export function useLeads() {
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase.from('leads').select('*').order('created_at', { ascending: false });
    setLeads((data || []).map((r) => rowToLead(r as Record<string, unknown>)));
    setLoading(false);
  }, []);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);

  const createLead = async (lead: Partial<Lead>) => {
    const { data, error } = await supabase.from('leads').insert({
      full_name: lead.name,
      email: lead.email,
      phone: lead.phone,
      country: lead.country,
      destination: lead.service,
      budget: lead.budgetNum || 0,
      status: mockStatusToDb[lead.status || 'New'],
      source: Object.entries(dbSourceToMock).find(([, v]) => v === lead.source)?.[0] || 'manual',
      tags: lead.tags || [],
      travel_date: lead.travelDate || null,
      pax_adults: lead.pax || 1,
    }).select().maybeSingle();
    if (!error && data) setLeads((prev) => [rowToLead(data as Record<string, unknown>), ...prev]);
    return { data, error };
  };

  const updateLeadStatus = async (id: string, status: LeadStatus) => {
    const { error } = await supabase.from('leads').update({ status: mockStatusToDb[status] }).eq('id', id);
    if (!error) setLeads((prev) => prev.map((l) => l.id === id ? { ...l, status } : l));
  };

  const deleteLead = async (id: string) => {
    const { error } = await supabase.from('leads').delete().eq('id', id);
    if (!error) setLeads((prev) => prev.filter((l) => l.id !== id));
  };

  return { leads, loading, refetch: fetchLeads, createLead, updateLeadStatus, deleteLead };
}
